// CrystalVault — Deposit & Withdraw Page

import { VAULT_CONFIGS, VAULT_ABI, ERC20_ABI, USDC_ADDRESS, USDC_DECIMALS, SHARE_DECIMALS, EXPLORER, APP_MODE } from '../config.js';
import { getAddress, getSigner, getReadProvider, getUsdcBalance, getHypeBalance } from '../wallet.js';
import { showTxModal, txModalSuccess, txModalError } from '../tx.js';
import { computeWithdrawalFee } from '../vault-math.js';
import { isStealth, isDepositor, canBypassStealth, launchDateLabel, startCountdown, xFollowUrl } from '../stealth-mode.js';
import { resumePendingZap } from '../zap-ui.js';
import { zapEntryHtml, wireZapEntry, usdcHelpTip, HYPE_BRIDGE_URL } from '../deposit-ui.js';
import { mountDepositWidget } from '../deposit-widget.js';

// ── Affiliate attribution ─────────────────────────────────────────────────
// If the user landed via /r/[code], r.html set a cookie `cc_affiliate_code`
// scoped to .crystalclear.finance (readable on app subdomain). After a
// deposit confirms, we tell the backend so the referral row gets written.
// The backend verifies the tx hash on-chain before recording — see
// core/affiliate/onchain_verify.py. Fire-and-forget so any hiccup doesn't
// surface as a deposit error.

const _AFFILIATE_COOKIE_NAME = 'cc_affiliate_code';

function _readAffiliateCookie() {
  const m = document.cookie.match(new RegExp('(?:^|; )' + _AFFILIATE_COOKIE_NAME + '=([^;]+)'));
  return m ? decodeURIComponent(m[1]) : null;
}

async function _maybeAttributeAffiliate({ depositorWallet, vaultAddr, depositBlock, depositTxHash }) {
  const linkCode = _readAffiliateCookie();
  if (!linkCode) return;  // organic deposit — no affiliate

  // Reverse-look-up vault key from address (same pattern as elsewhere in this file)
  const vc = VAULT_CONFIGS.find(v => v.address?.toLowerCase() === vaultAddr.toLowerCase());
  if (!vc) {
    console.warn('affiliate attribute: unknown vault address', vaultAddr);
    return;
  }

  // Affiliate API lives on Lightsail (crystalclear.finance) — Lambda
  // doesn't serve /api/affiliate/* yet (SQLite-only state until the DDB-
  // sync sidecar is extended). CORS is open on the Lightsail API.
  const AFFILIATE_API = 'https://crystalclear.finance/api/affiliate';
  try {
    const res = await fetch(`${AFFILIATE_API}/attribute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        depositor_wallet: depositorWallet,
        vault: vc.key,
        link_code: linkCode,
        deposit_block: depositBlock,
        deposit_tx_hash: depositTxHash,
      }),
    });
    if (res.ok) {
      console.info('affiliate attribute: ok', { vault: vc.key, link: linkCode });
    } else if (res.status === 409) {
      // already attributed (this depositor was previously attributed to
      // either this affiliate or a different one). Not an error.
      console.info('affiliate attribute: already attributed');
    } else if (res.status === 403) {
      // on-chain verification failed — could be a stale cookie or someone
      // tampered with it. Log but don't surface to the user.
      console.warn('affiliate attribute: on-chain verification failed', await res.text());
    } else {
      console.warn('affiliate attribute: HTTP', res.status, await res.text());
    }
  } catch (e) {
    console.warn('affiliate attribute: network error', e);
  }
}


// Stealth mode: replace the whole Deposit page with a single launch block.
function renderStealthDeposit() {
  return `
  <div class="flex flex-col items-center justify-center py-20 px-6 text-center">
    <div class="w-16 h-16 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
      <span class="material-symbols-outlined text-primary text-3xl">lock_clock</span>
    </div>
    <h2 class="font-headline font-bold text-3xl text-on-surface mb-3">
      Deposits open <span class="text-primary">${launchDateLabel()}</span>
    </h2>
    <p class="text-on-surface-variant max-w-md mb-10">
      Onyx and Amber are trading live today. Performance metrics and deposits
      unlock at launch.
    </p>
    <div id="deposit-countdown" class="mb-10"></div>
    <a href="${xFollowUrl()}" target="_blank" rel="noopener"
       class="px-6 py-3 bg-gradient-to-br from-primary to-primary-container text-on-primary-container font-bold hover:scale-105 transition-all flex items-center gap-2">
      <span class="material-symbols-outlined">notifications_active</span>
      Follow ${APP_MODE.x_handle} for launch
    </a>
    <a href="#overview" class="mt-6 text-sm text-secondary hover:text-primary transition-colors">
      ← Back to overview
    </a>
  </div>
  `;
}

export function renderDeposit() {
  if (isStealth()) return renderStealthDeposit();
  return _renderDepositLive();
}

function _renderDepositLive() {
  // Build gemstone vault selector cards. Coming-soon vaults are visible but disabled.
  const liveVaults = VAULT_CONFIGS.filter(v => v.status === "live" && v.address);
  // Default-select the first OPEN vault (not deposit_closed) so a depositor
  // lands on a working deposit form, not a "closed" one. Falls back to the
  // first live vault if all are closed (existing depositors can still withdraw).
  const openVaults = liveVaults.filter(v => !v.deposit_closed);
  const defaultAddr = (openVaults[0] || liveVaults[0])?.address || "";

  const vaultCards = VAULT_CONFIGS.map((vc, i) => {
    const isLive = vc.status === "live" && vc.address;
    const isSelected = isLive && vc.address === defaultAddr;
    const baseClasses = `vault-select-card flex items-center gap-3 p-3 rounded-lg border-2 transition-all`;
    const stateClasses = isSelected
      ? `border-[${vc.color.hex}]/60 ${vc.color.bg10}`
      : isLive
        ? `border-outline-variant/10 hover:border-[${vc.color.hex}]/30 cursor-pointer`
        : `border-outline-variant/10 opacity-50 cursor-not-allowed`;
    return `
      <button class="${baseClasses} ${stateClasses}"
              data-vault="${vc.address || ''}" data-idx="${i}" data-live="${isLive ? '1' : '0'}"
              ${isLive ? '' : 'disabled'}>
        <div class="w-9 h-9 rounded-lg ${vc.color.bg20} flex items-center justify-center shrink-0">
          <span class="material-symbols-outlined ${vc.color.text} text-lg">${vc.icon}</span>
        </div>
        <div class="text-left">
          <p class="font-headline font-bold text-sm text-on-surface">${vc.name}</p>
          <p class="text-[0.6rem] text-secondary/50">${isLive ? vc.subtitle : 'Coming soon'}</p>
        </div>
      </button>
    `;
  }).join("");

  return `
  <h2 class="font-headline font-bold text-2xl text-on-surface mb-6">Deposit & Withdraw</h2>

  <!-- Legacy→Lagoon transition banner. The founding-fleet vaults are closed to
       NEW deposits; new deposits will route to the audited Lagoon vaults once
       live. Existing depositors withdraw here as normal. Shown whenever every
       live vault is deposit_closed (current transition state). -->
  <div id="deposit-transition-banner" class="hidden mb-6 p-4 rounded-lg border border-primary/25 bg-surface-container-low flex items-start gap-3">
    <span class="material-symbols-outlined text-primary shrink-0" style="font-size:20px;line-height:1.2;">swap_horiz</span>
    <div class="text-sm text-on-surface-variant leading-relaxed">
      <span class="text-on-surface font-semibold">New deposits are moving to our audited Lagoon vaults.</span>
      The founding-fleet vaults below are closed to new deposits — the strategy track record stays
      <a href="#vaults" class="text-primary hover:underline">live &amp; verifiable</a>.
      Existing positions and withdrawals are unaffected; withdraw anytime on the right.
    </div>
  </div>

  <!-- Vault Selector (gemstone cards) — 2 rows x 5 cols on desktop, responsive
       down to 2 cols on mobile. Grid enforces equal width per column so all
       10 pills are visually uniform regardless of gemstone name length. -->
  <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-6" id="vault-selector">
    ${vaultCards}
  </div>
  <input type="hidden" id="vault-select" value="${defaultAddr}" />

  <!-- Vault Info Bar -->
  <div class="flex gap-4 mb-6 flex-wrap" id="vault-info-bar">
    <div class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-container-high border border-outline-variant/10 text-xs">
      <span class="text-secondary/80">Share Price</span>
      <span class="text-primary font-semibold" id="di-share-price">$1.00</span>
    </div>
    <div class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-container-high border border-outline-variant/10 text-xs">
      <span class="text-secondary/80">TVL</span>
      <span class="text-on-surface font-semibold" id="di-tvl">$0.00</span>
    </div>
    <div class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-container-high border border-outline-variant/10 text-xs">
      <span class="text-secondary/80">Your Shares</span>
      <span class="text-on-surface font-semibold" id="di-shares">0.0000</span>
    </div>
    <div class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-container-high border border-outline-variant/10 text-xs">
      <span class="text-secondary/80">Perf Fee</span>
      <span class="text-primary font-semibold" id="di-perf-fee">—</span>
    </div>
  </div>

  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
    <!-- Deposit -->
    <div id="dep-card" class="bg-surface-container rounded-lg border border-outline-variant/10 p-6 flex flex-col">
      <h3 class="font-headline font-bold text-lg text-on-surface mb-4 flex items-center gap-2">
        <span class="material-symbols-outlined text-primary">arrow_downward</span>
        Deposit USDC
      </h3>
      <div class="mb-4">
        <div class="flex justify-between text-xs text-secondary/60 mb-1">
          <span>Amount</span>
          <span class="text-right leading-tight">
            <span class="block">Balance: <span id="dep-usdc-bal" class="text-primary">0.00</span> USDC</span>
            <span class="block text-secondary/50">Gas: <span id="dep-hype-bal" class="text-primary">0.000</span> HYPE</span>
          </span>
        </div>
        <div class="flex items-center bg-surface-container-high rounded-lg border border-outline-variant/20 overflow-hidden">
          <input type="number" id="dep-amount" placeholder="0.00" class="flex-1 bg-transparent px-4 py-3 text-lg font-headline text-on-surface outline-none" />
          <button id="dep-max-btn" class="px-3 py-1 mr-2 text-xs text-primary border border-primary/30 rounded hover:bg-primary/10 transition-colors">MAX</button>
          <span class="pr-4 text-sm text-secondary">USDC</span>
        </div>
        <!-- Informational only: explains "insufficient funds" before MetaMask
             can throw it. Never disables the Deposit button — user might know
             they have HYPE arriving, or want to try anyway. -->
        <div id="dep-balance-tip" class="hidden text-xs mt-2 p-2 rounded border border-secondary-fixed-dim/30 bg-secondary-fixed-dim/5 text-on-surface-variant flex items-start gap-2">
          <span class="material-symbols-outlined text-secondary-fixed-dim text-base shrink-0" style="font-size:18px;line-height:1;">info</span>
          <span id="dep-balance-tip-msg"></span>
        </div>
      </div>
      <div class="text-xs text-secondary/60 mb-1">
        You will receive: <span id="dep-preview" class="text-on-surface font-semibold">0.0000</span> shares
      </div>
      <!-- Share-based vault accounting note. Pre-empts the "I deposited $20
           5 min ago and already see profit/loss" confusion: shares are
           minted at the vault's current share price (which already reflects
           the value of any open trades), so the user's P&L moves with the
           vault from THIS moment forward — not from the trades' original
           entry prices. Plain English, no math. -->
      <div class="text-[0.65rem] text-secondary/50 italic mb-3 leading-snug">
        Shares are minted at the vault's current price, which already
        reflects all open trades. Your P&amp;L = (your share value now) −
        (USDC you deposited).
      </div>
      <div id="dep-min-hint" class="text-[0.65rem] mb-2 text-secondary/40">
        Minimum: <span id="dep-min-amount">$10</span>
      </div>
      <!-- Zap entry (shared with vault-detail via deposit-ui.js) -->
      ${zapEntryHtml('dep-zap-btn')}
      <div id="dep-error" class="hidden text-xs text-error mb-3 flex items-center gap-1">
        <span class="material-symbols-outlined text-sm">warning</span>
        <span id="dep-error-msg"></span>
      </div>
      <button id="dep-btn" class="mt-auto w-full py-3 bg-gradient-to-br from-primary to-primary-container text-on-primary font-headline font-bold rounded-DEFAULT hover:shadow-[0_0_24px_rgba(138,235,255,0.4)] transition-all duration-300 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none">
        Deposit
      </button>
    </div>

    <!-- Withdraw -->
    <div class="bg-surface-container rounded-lg border border-outline-variant/10 p-6 flex flex-col">
      <h3 class="font-headline font-bold text-lg text-on-surface mb-4 flex items-center gap-2">
        <span class="material-symbols-outlined text-primary">arrow_upward</span>
        Withdraw
      </h3>
      <div class="mb-4">
        <div class="flex justify-between text-xs text-secondary/60 mb-1">
          <span>Shares to redeem</span>
          <span class="text-right leading-tight">
            <span class="block">Balance: <span id="wd-shares-bal" class="text-primary">0.0000</span></span>
            <span class="block text-secondary/50">Gas: <span id="wd-hype-bal" class="text-primary">0.000</span> HYPE</span>
          </span>
        </div>
        <div class="flex items-center bg-surface-container-high rounded-lg border border-outline-variant/20 overflow-hidden">
          <input type="number" id="wd-shares" placeholder="0.00" class="flex-1 bg-transparent px-4 py-3 text-lg font-headline text-on-surface outline-none" />
          <button id="wd-max-btn" class="px-3 py-1 mr-2 text-xs text-primary border border-primary/30 rounded hover:bg-primary/10 transition-colors">MAX</button>
          <span class="pr-4 text-sm text-secondary">shares</span>
        </div>
        <!-- Withdraws also spend HYPE for gas; same informational pattern as
             the deposit card. Never gates the request-withdrawal button. -->
        <div id="wd-balance-tip" class="hidden text-xs mt-2 p-2 rounded border border-secondary-fixed-dim/30 bg-secondary-fixed-dim/5 text-on-surface-variant flex items-start gap-2">
          <span class="material-symbols-outlined text-secondary-fixed-dim text-base shrink-0" style="font-size:18px;line-height:1;">info</span>
          <span id="wd-balance-tip-msg"></span>
        </div>
      </div>
      <div class="text-xs text-secondary/60 mb-1 flex items-center gap-1">
        You will receive: <span id="wd-preview" class="text-on-surface font-semibold">0.00</span> USDC
        <span id="wd-info" class="material-symbols-outlined text-secondary/40 text-sm cursor-help hidden" title="">info</span>
      </div>
      <p id="wd-perf-note" class="hidden text-[0.6rem] text-secondary/50 leading-snug mb-3"></p>
      <div class="mb-3"></div>
      <button id="wd-btn" class="mt-auto w-full py-3 bg-gradient-to-br from-primary to-primary-container text-on-primary font-headline font-bold rounded-DEFAULT hover:shadow-[0_0_24px_rgba(138,235,255,0.4)] transition-all duration-300 active:scale-[0.98]">
        Request Withdrawal
      </button>

      <!-- Pending claim -->
      <div id="claim-box" class="hidden mt-4 p-4 rounded-lg border border-tertiary/20 bg-tertiary/5">
        <p class="text-sm text-tertiary font-semibold mb-1">Pending Withdrawal</p>
        <p class="text-xs text-secondary/80 mb-2 flex items-center gap-1">
          <span id="claim-amount">0</span> USDC ready to claim
          <span id="claim-info" class="material-symbols-outlined text-secondary/40 text-sm cursor-help hidden" title="">info</span>
        </p>
        <button id="claim-btn" class="mt-2 w-full py-2 bg-tertiary/20 hover:bg-tertiary/30 border border-tertiary/30 rounded-lg text-tertiary font-headline font-semibold text-sm">
          Claim USDC
        </button>
      </div>
    </div>
  </div>
  `;
}

export async function initDeposit() {
  // Stealth: depositors get the full live UI (withdraw + claim still work).
  // New deposits stay locked for everyone (incl. depositors) EXCEPT wallets
  // on APP_MODE.deposit_whitelist — those testers see the same live UI but
  // their CTA stays enabled so they can actually deposit pre-launch.
  if (isStealth()) {
    const addr = getAddress();
    const onWhitelist = canBypassStealth(addr);
    if (addr && (onWhitelist || await isDepositor(addr))) {
      document.getElementById("page-content").innerHTML = _renderDepositLive();
      await _initDepositLive();
      if (!onWhitelist) _lockDepositCTAForStealth();
      return;
    }
    startCountdown(document.getElementById("deposit-countdown"));
    return;
  }
  await _initDepositLive();
}

// Swap deposit button into a visibly-locked "opens <date>" state. Withdraw +
// claim buttons stay untouched — existing depositors can still exit at any
// time. Idempotent; runs exactly once per page render.
function _lockDepositCTAForStealth() {
  const btn = document.getElementById("dep-btn");
  if (!btn) return;
  btn.disabled = true;
  btn.textContent = `Opens ${launchDateLabel()}`;
  btn.classList.add("opacity-40", "cursor-not-allowed");
  // Replace the min-deposit hint with a stealth-mode note
  const hint = document.getElementById("dep-min-hint");
  if (hint) {
    hint.innerHTML = `<span class="text-tertiary">New deposits open ${launchDateLabel()}.</span> Withdraw &amp; claim remain available.`;
  }
}

async function _initDepositLive() {
  const addr = getAddress();
  const select = document.getElementById("vault-select");
  const refreshVault = () => addr
    ? _refreshVaultState(select.value, addr)
    : _refreshVaultPublic(select.value);

  // Vault card selection — only "live" cards are clickable (coming-soon are disabled).
  // Wired regardless of wallet state so a disconnected visitor can still
  // browse vault info before committing to connect.
  document.querySelectorAll(".vault-select-card").forEach(card => {
    if (card.dataset.live !== "1") return;
    card.addEventListener("click", () => {
      select.value = card.dataset.vault;
      document.querySelectorAll(".vault-select-card").forEach(c => {
        const idx = parseInt(c.dataset.idx);
        const vc = VAULT_CONFIGS[idx];
        const isLive = c.dataset.live === "1";
        c.className = isLive
          ? `vault-select-card flex items-center gap-3 p-3 rounded-lg border-2 transition-all border-outline-variant/10 hover:border-[${vc.color.hex}]/30 cursor-pointer`
          : `vault-select-card flex items-center gap-3 p-3 rounded-lg border-2 transition-all border-outline-variant/10 opacity-50 cursor-not-allowed`;
      });
      const idx = parseInt(card.dataset.idx);
      const vc = VAULT_CONFIGS[idx];
      card.className = `vault-select-card flex items-center gap-3 p-3 rounded-lg border-2 transition-all border-[${vc.color.hex}]/60 ${vc.color.bg10}`;
      refreshVault();
      _applyDepositGate(select.value);
      if (_useDepWidget) _mountDepWidget(select.value);
    });
  });

  // Adaptive deposit widget (behind ?dw=1) — mount for the initial selection.
  if (_useDepWidget) _mountDepWidget(select.value);

  // Amount inputs + MAX buttons — wire always; previews don't need a wallet.
  document.getElementById("dep-amount").addEventListener("input", () => _updateDepPreview(select.value));
  // Input handler also clears maxRaw — if the user clicks MAX then types,
  // the raw stash is stale and should not be used by _withdraw.
  document.getElementById("wd-shares").addEventListener("input", () => {
    delete document.getElementById("wd-shares").dataset.maxRaw;
    _updateWdPreview(select.value);
  });
  document.getElementById("dep-max-btn").addEventListener("click", () => {
    document.getElementById("dep-amount").value = document.getElementById("dep-usdc-bal").textContent;
    _updateDepPreview(select.value);
  });
  // Withdraw MAX: prefer the stashed raw BigInt over the toFixed(4) display
  // text. The display rounds half-up, so .toFixed(4) of a balance like
  // 1.23456 prints "1.2346" — re-parsing that float overshoots the actual
  // balance by ~5e-5 shares, which makes requestWithdraw revert. By
  // stashing the raw BigInt on the input as `maxRaw`, _withdraw can pass
  // the exact balance instead of a float roundtrip. See bug-report
  // 2026-05-19, tester wallet 0x0984F.
  document.getElementById("wd-max-btn").addEventListener("click", () => {
    const balEl = document.getElementById("wd-shares-bal");
    const input = document.getElementById("wd-shares");
    const raw = balEl.dataset.raw;
    if (raw && raw !== "0") {
      // Display the full-precision human value (no toFixed!) so the user
      // sees what they're submitting. Raw is the source of truth though.
      input.value = (Number(BigInt(raw)) / 10 ** SHARE_DECIMALS).toString();
      input.dataset.maxRaw = raw;
    } else {
      input.value = "0";
      delete input.dataset.maxRaw;
    }
    _updateWdPreview(select.value);
  });

  if (addr) {
    // Connected: normal action wiring.
    document.getElementById("dep-btn").addEventListener("click", () => _deposit(select.value, addr));
    document.getElementById("wd-btn").addEventListener("click", () => _withdraw(select.value, addr));
    document.getElementById("claim-btn").addEventListener("click", () => _claim(select.value));
  } else {
    // Disconnected: action buttons route to the top-nav Connect Wallet
    // handler instead of attempting a sign. Labels flipped in the DOM
    // so the affordance is obvious ("Connect Wallet" not "Deposit").
    _flipActionButtonsToConnect();
  }

  refreshVault();

  // Zap entry — open the "fund your HyperEVM wallet" wizard. Wired regardless of
  // wallet state; the wizard handles the disconnected case.
  wireZapEntry('dep-zap-btn');

  // X8: if a bridge was in flight when the user last left, resume tracking it
  // (re-opens the "Bridging…" view + restores the progress pill).
  resumePendingZap();

  // Apply the deposit gate to the initially-selected vault (also toggles the
  // transition banner based on whether THAT vault is closed).
  _applyDepositGate(select.value);
}

// Deposit gate (legacy→Lagoon transition). The vault selector is SHARED by the
// deposit + withdraw columns — existing depositors must still select a closed
// vault to WITHDRAW from it — so we gate the deposit ACTION, never the selector.
// When the selected vault is deposit_closed: disable the deposit input + button
// and explain. The withdraw column is left 100% untouched. config flag only;
// flipping deposit_closed:false reopens a vault with zero code change.
function _applyDepositGate(vaultAddr) {
  const vc = VAULT_CONFIGS.find(v => v.address?.toLowerCase() === (vaultAddr || "").toLowerCase());
  const closed = !!(vc && vc.deposit_closed === true);
  // Transition banner reflects the SELECTED vault (closed → show; open → hide).
  const banner = document.getElementById("deposit-transition-banner");
  if (banner) banner.classList.toggle("hidden", !closed);
  const btn = document.getElementById("dep-btn");
  const amt = document.getElementById("dep-amount");
  const maxBtn = document.getElementById("dep-max-btn");
  const note = document.getElementById("dep-min-hint");
  if (!btn) return;
  // Stealth has its own deposit lock — don't fight it.
  if (isStealth()) return;
  if (closed) {
    // Closed wins regardless of connection state: "Closed to new deposits" is
    // more honest than "Connect Wallet" on a form that can't accept deposits.
    // disabled=true suppresses BOTH the deposit handler and the disconnected
    // connect-wallet flip, so no handler surgery is needed.
    btn.disabled = true;
    btn.textContent = "Closed to new deposits";
    if (amt) { amt.disabled = true; amt.value = ""; amt.placeholder = "Deposits closed"; }
    if (maxBtn) maxBtn.disabled = true;
    if (note) note.innerHTML = `<span class="text-on-surface-variant">${vc.name} is closed to new deposits during the Lagoon transition — <span class="text-on-surface">withdraw remains available</span> on the right.</span>`;
  } else if (getAddress()) {
    // Open vault + connected → restore normal deposit state. When disconnected,
    // leave the "Connect Wallet" flip untouched.
    btn.disabled = false;
    btn.textContent = "Deposit";
    if (amt) { amt.disabled = false; amt.placeholder = "0.00"; }
    if (maxBtn) maxBtn.disabled = false;
  }
}

// Disconnected action-button UI. Users can still see the vault, type an
// amount, see estimated shares — but the commit buttons advertise what
// actually has to happen first.
function _flipActionButtonsToConnect() {
  const connect = () => document.getElementById("connect-btn")?.click();
  ["dep-btn", "wd-btn", "claim-btn"].forEach(id => {
    const el = document.getElementById(id);
    if (!el) return;
    el.textContent = "Connect Wallet";
    el.addEventListener("click", connect);
  });
}

// Public state only — share price + TVL + perf fee. Doesn't touch wallet.
// Used on the disconnected preview; the full _refreshVaultState adds the
// user-scoped numbers (shares, cost basis, USDC balance, withdrawal req).
async function _refreshVaultPublic(vaultAddr) {
  if (!vaultAddr) return;
  const rp = getReadProvider();
  const vault = new ethers.Contract(vaultAddr, VAULT_ABI, rp);
  try {
    const [totalAssets, totalSupply, perfFee, minDepositRaw] = await Promise.all([
      vault.totalAssets(),
      vault.totalSupply(),
      vault.performanceFeeBps(),
      vault.minDeposit(),
    ]);
    const tvl = Number(totalAssets) / 10 ** USDC_DECIMALS;
    const vc = VAULT_CONFIGS.find(v => v.address?.toLowerCase() === vaultAddr.toLowerCase());
    let sharePrice = 1.0;
    if (totalSupply > 0n) {
      const assetsPerShare = await vault.convertToAssets(10n ** BigInt(SHARE_DECIMALS));
      const rawPrice = Number(assetsPerShare) / 10 ** USDC_DECIMALS;
      sharePrice = rawPrice / ((vc?.launchSharePrice) || 1.0);
    }
    document.getElementById("di-share-price").textContent = "$" + sharePrice.toFixed(4);
    document.getElementById("di-tvl").textContent = "$" + tvl.toFixed(2);
    document.getElementById("di-shares").textContent = "—";
    document.getElementById("di-perf-fee").textContent = (Number(perfFee) / 100).toFixed(0) + "%";
    document.getElementById("dep-usdc-bal").textContent = "—";
    _clearHypeDisplay();
    const minDepositHuman = Number(minDepositRaw) / 10 ** USDC_DECIMALS;
    // Whole-dollar format (no decimal) — matches the demoted hint copy
// "Minimum: $10" instead of the prior front-and-center "$10.00" framing.
document.getElementById("dep-min-amount").textContent = "$" + Math.round(minDepositHuman);
  } catch (e) {
    console.warn("deposit: public refresh failed", e);
  }
}

async function _refreshVaultState(vaultAddr, userAddr) {
  const rp = getReadProvider();
  const vault = new ethers.Contract(vaultAddr, VAULT_ABI, rp);

  const [totalAssets, totalSupply, perfFee, shares, costBasis, withdrawReq, minDepositRaw] = await Promise.all([
    vault.totalAssets(),
    vault.totalSupply(),
    vault.performanceFeeBps(),
    vault.balanceOf(userAddr),
    vault.userCostBasis(userAddr),
    vault.withdrawalRequests(userAddr),
    vault.minDeposit(),
  ]);

  const tvl = Number(totalAssets) / 10 ** USDC_DECIMALS;
  const vc = VAULT_CONFIGS.find(v => v.address?.toLowerCase() === vaultAddr.toLowerCase());
  let sharePrice = 1.0;
  if (totalSupply > 0n) {
    const assetsPerShare = await vault.convertToAssets(10n ** BigInt(SHARE_DECIMALS));
    const rawPrice = Number(assetsPerShare) / 10 ** USDC_DECIMALS;
    sharePrice = rawPrice / ((vc?.launchSharePrice) || 1.0);
  }

  document.getElementById("di-share-price").textContent = "$" + sharePrice.toFixed(4);
  document.getElementById("di-tvl").textContent = "$" + tvl.toFixed(2);
  document.getElementById("di-shares").textContent = (Number(shares) / 10 ** SHARE_DECIMALS).toFixed(4);
  document.getElementById("di-perf-fee").textContent = (Number(perfFee) / 100).toFixed(0) + "%";
  // Stash the raw BigInt share count on the balance element. MAX uses this
  // (not the displayed toFixed(4) text) to fill the input — without it, MAX
  // could write a string that round-trips to a BigInt LARGER than balanceOf,
  // causing requestWithdraw to revert with ZeroAmount(). Bug found 2026-05-19
  // on tester wallet 0x0984F / Diamond.
  const wdBalEl = document.getElementById("wd-shares-bal");
  wdBalEl.textContent = (Number(shares) / 10 ** SHARE_DECIMALS).toFixed(4);
  wdBalEl.dataset.raw = shares.toString();

  // USDC + HYPE in parallel — they're independent reads and the HYPE one
  // doesn't gate the deposit flow, but we want both on screen at the same
  // time so the user can act on the missing piece immediately.
  const [usdcBal, hypeBal] = await Promise.all([getUsdcBalance(), getHypeBalance()]);
  document.getElementById("dep-usdc-bal").textContent = usdcBal.toFixed(2);
  _renderHypeCues(usdcBal, hypeBal, userAddr);

  const minDepositHuman = Number(minDepositRaw) / 10 ** USDC_DECIMALS;
  // Whole-dollar format (no decimal) — matches the demoted hint copy
// "Minimum: $10" instead of the prior front-and-center "$10.00" framing.
document.getElementById("dep-min-amount").textContent = "$" + Math.round(minDepositHuman);
  document.getElementById("dep-amount").dataset.minDeposit = minDepositHuman;

  // Pending withdrawal. V3.6 LOCKS the gross-asset payout at request time via
  // withdrawalRequests(addr).snapshottedAssets (withdrawReq[4]). That value is
  // what claimWithdraw will pay — use it directly. The V3.4-era live recompute
  // (shares * (totalAssets+1) / (totalSupply+shares+1000)) diverges from the
  // actual payout as totalAssets drifts post-request. Diamond user 2026-05-19
  // saw $10.26 in the UI while contract was about to pay $24.02.
  // Legacy pre-V3.6 pending requests have snapshottedAssets == 0 — fall back
  // to the recompute for those only.
  const pendingShares = withdrawReq[0];
  if (pendingShares > 0n) {
    const costBasisSnap = withdrawReq[1];
    const totalSharesSnap = withdrawReq[2];
    const snapshottedAssets = withdrawReq[4];
    let grossAssets;
    if (snapshottedAssets > 0n) {
      grossAssets = snapshottedAssets;
    } else {
      grossAssets = (pendingShares * (totalAssets + 1n)) / (totalSupply + pendingShares + 1000n);
    }
    const fee = computeWithdrawalFee(pendingShares, totalSharesSnap, costBasisSnap, grossAssets, perfFee);
    const net = grossAssets - fee;
    document.getElementById("claim-box").classList.remove("hidden");
    document.getElementById("claim-amount").textContent = (Number(net) / 10 ** USDC_DECIMALS).toFixed(2);
    const infoIcon = document.getElementById("claim-info");
    const tail = snapshottedAssets > 0n
      ? "Amount is locked: this is exactly what the contract will pay on claim."
      : "Amount fluctuates with vault NAV until claimed.";  // legacy pre-V3.6 pending request
    if (fee > 0n && infoIcon) {
      const grossH = (Number(grossAssets) / 10 ** USDC_DECIMALS).toFixed(4);
      const feeH = (Number(fee) / 10 ** USDC_DECIMALS).toFixed(4);
      const netH = (Number(net) / 10 ** USDC_DECIMALS).toFixed(4);
      infoIcon.setAttribute("title", `Gross $${grossH} − perf fee $${feeH} = net $${netH}. ${tail}`);
      infoIcon.classList.remove("hidden");
    } else if (infoIcon) {
      infoIcon.setAttribute("title", tail);
      infoIcon.classList.remove("hidden");
    }
  } else {
    document.getElementById("claim-box").classList.add("hidden");
  }
}

// Threshold for "you have enough HYPE for gas". Typical HyperEVM tx burns
// ~0.001 HYPE; 0.005 = ~5 deposits or ~10 withdrawals worth of headroom.
// Below this we show the bridge prompt; at or above we stay silent.
const HYPE_GAS_THRESHOLD = 0.005;
// HYPE_BRIDGE_URL now lives in deposit-ui.js (shared with vault-detail.js).

// ?dw=1 (in search OR hash, e.g. app.html?dw=1#deposit) → mount the adaptive
// deposit widget in place of the legacy form. No flag = page byte-identical.
function _hasFlag(name) {
  try {
    if (new URLSearchParams(location.search).get(name) === "1") return true;
    const h = location.hash.includes("?") ? location.hash.split("?")[1] : "";
    return new URLSearchParams(h).get(name) === "1";
  } catch { return false; }
}
const _useDepWidget = _hasFlag("dw");
function _mountDepWidget(vaultKey) {
  const card = document.getElementById("dep-card");
  if (!card) return;
  // Hide the legacy form (keep it in the DOM so its refresh code never hits null).
  Array.from(card.children).forEach((c, i) => { if (i > 0 && c.id !== "dw-mount") c.style.display = "none"; });
  let mount = document.getElementById("dw-mount");
  if (!mount) { mount = document.createElement("div"); mount.id = "dw-mount"; card.appendChild(mount); }
  mountDepositWidget(mount, vaultKey);
}

// ── Bridged-stable detection ─────────────────────────────────────────────
// A real depositor bridged $125 via Jumper, got a NON-native stable on
// HyperEVM, saw "$0 USDC" with no explanation, and bounced. When the wallet
// has zero of OUR USDC (the vault's asset, 0xb883…), scan their ERC-20s via
// Hyperscan and explain what they actually hold + how to convert.
//
// ALLOWLIST ONLY for dollar-amount claims: token symbols/names are
// self-reported, so a scam token named "USDC" could otherwise make us tell
// the user they have money. The three below are the verified liquid stables
// on HyperEVM (holders per Hyperscan 2026-06-10: USD₮0 55k, USDe 8.6k,
// USDHL 8.4k). NB: USD₮0 is omnichain TETHER (USDT0), not a USDC variant —
// it's just the token bridgers most commonly end up holding, since HyperEVM
// has no native Circle USDC. Anything else USD-named
// is listed WITHOUT a value claim. Vault/lending receipt tokens (hbUSDC,
// HyperLend a/debt-tokens…) are deliberately not in the allowlist.
const BRIDGED_STABLES = {
  "0xb8ce59fc3717ada4c02eadf9682a9e934f625ebb": { symbol: "USD₮0", decimals: 6,  family: "a bridged Tether-family stablecoin (USDT0)" },
  "0x5d3a1ff2b6bab83b63cd9ad0787074081a52ef34": { symbol: "USDe",  decimals: 18, family: "Ethena's synthetic-dollar stablecoin" },
  "0xb50a96253abdf803d85efcdce07ad8becbc52bd5": { symbol: "USDHL", decimals: 6,  family: "the Hyper USD stablecoin" },
};
const HYPERSWAP_URL = `https://app.hyperswap.exchange/#/swap?outputCurrency=${USDC_ADDRESS}`;
const HYPERSCAN_API = "https://www.hyperscan.com/api/v2";

// Scan the wallet's ERC-20s on Hyperscan. Returns
//   { known: [{symbol, human, address}], other: [{symbol, address}] }
// or null on any failure (timeout, CORS, rate limit) — callers treat null
// as "scan unavailable" and keep the generic guidance. Display-only helper:
// never throws, never blocks the deposit flow.
async function _scanBridgedStables(addr) {
  try {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 5000);
    const r = await fetch(`${HYPERSCAN_API}/addresses/${addr}/token-balances`, { signal: ctl.signal });
    clearTimeout(timer);
    if (!r.ok) return null;
    const items = await r.json();
    if (!Array.isArray(items)) return null;
    // Our own vault share tokens (cvUSDC, ccAMBER, …) are USD-named ERC-20s —
    // a fully-deposited wallet (USDC=0, holds shares) must NOT be told its own
    // shares are an "unrecognised token".
    const ourVaults = new Set(VAULT_CONFIGS.filter(v => v.address).map(v => v.address.toLowerCase()));
    const known = [], other = [];
    for (const it of items) {
      const t = it.token || {};
      const taddr = (t.address || t.address_hash || "").toLowerCase();
      const raw = it.value;
      if (!taddr || !raw || raw === "0" || (t.type && t.type !== "ERC-20")) continue;
      if (ourVaults.has(taddr)) continue;
      const wl = BRIDGED_STABLES[taddr];
      if (wl) {
        const human = Number(raw) / 10 ** wl.decimals;
        if (human >= 0.01) known.push({ symbol: wl.symbol, human, address: taddr, family: wl.family });
      } else if ((t.symbol || "").toLowerCase().includes("usd")) {
        if (other.length < 3) other.push({ symbol: t.symbol, address: taddr });
      }
    }
    known.sort((a, b) => b.human - a.human);
    return { known, other };
  } catch {
    return null;
  }
}

// Upgrade the deposit-card tip once the Hyperscan scan resolves. Only runs
// when the wallet has zero native USDC; only ever ADDS information.
async function _upgradeNoUsdcTip(addr) {
  const scan = await _scanBridgedStables(addr);
  if (!scan) return;
  const short = (a) => `${a.slice(0, 6)}…${a.slice(-4)}`;
  const convertLinks =
    `Swap it for native USDC on <a href="${HYPERSWAP_URL}" target="_blank" rel="noopener" class="text-primary underline">HyperSwap</a>, ` +
    `or deposit USDC on <a href="https://app.hyperliquid.xyz/portfolio" target="_blank" rel="noopener" class="text-primary underline">Hyperliquid</a> ` +
    `and use "Transfer to EVM".`;
  if (scan.known.length > 0) {
    const list = scan.known
      .map((k) => `<span class="font-semibold text-on-surface">$${k.human.toFixed(2)} ${k.symbol}</span> — ${k.family} (<span class="font-mono">${short(k.address)}</span>)`)
      .join("; ");
    _setBalanceTip("dep", `You have ${list} in this wallet — not the ` +
      `<span class="font-semibold text-on-surface">native HL USDC</span> (<span class="font-mono">${short(USDC_ADDRESS.toLowerCase())}</span>) our vaults accept. ${convertLinks}`);
  } else if (scan.other.length > 0) {
    const list = scan.other
      .map((k) => `${k.symbol} (<span class="font-mono">${short(k.address)}</span>)`)
      .join(", ");
    _setBalanceTip("dep", `This wallet holds USD-named tokens we don't recognise as liquid stables: ${list}. ` +
      `Our vaults accept only <span class="font-semibold text-on-surface">native HL USDC</span> ` +
      `(<span class="font-mono">${short(USDC_ADDRESS.toLowerCase())}</span>) — verify yours on ` +
      `<a href="https://www.hyperscan.com/address/${addr}?tab=tokens" target="_blank" rel="noopener" class="text-primary underline">Hyperscan</a>. ${convertLinks}`);
  }
  // No stables found at all → leave the existing how-to-deposit guidance.
}

// Render the HYPE balance + decide which informational cue (if any) to show
// on BOTH the deposit and withdraw cards. Never disables buttons — this is
// strictly an "inform the user what MetaMask is about to complain about"
// surface. State matrix per the task spec:
//   USDC > 0  +  HYPE ≥ 0.005   → silent (both cards)
//   USDC > 0  +  HYPE < 0.005   → "low HYPE for gas" tip on both cards
//   USDC = 0  +  HYPE any       → "no USDC" tip on deposit card; HYPE-only
//                                  tip on withdraw if HYPE also low (since
//                                  withdraws don't need USDC)
function _renderHypeCues(usdcBal, hypeBal, userAddr) {
  const lowHype = hypeBal < HYPE_GAS_THRESHOLD;
  const noUsdc = usdcBal <= 0;

  // Format HYPE to 4dp when low (so 0.001 reads clearly) else 3dp.
  const hypeFmt = lowHype ? hypeBal.toFixed(4) : hypeBal.toFixed(3);
  const colorCls = lowHype ? "text-error" : "text-primary";
  const depHype = document.getElementById("dep-hype-bal");
  const wdHype = document.getElementById("wd-hype-bal");
  if (depHype) { depHype.textContent = hypeFmt; depHype.className = colorCls; }
  if (wdHype)  { wdHype.textContent  = hypeFmt; wdHype.className  = colorCls; }

  // Deposit-card tip (shared copy — see deposit-ui.js usdcHelpTip, zap-first).
  const depMsg = usdcHelpTip({ noUsdc, lowHype });
  _setBalanceTip("dep", depMsg);

  // Zero native USDC → kick the async Hyperscan scan; if the wallet holds a
  // bridged stable, the tip above gets UPGRADED in place with what they
  // actually have + conversion paths. Fire-and-forget: scan failure leaves
  // the generic guidance untouched.
  if (noUsdc && userAddr) _upgradeNoUsdcTip(userAddr);

  // Withdraw-card tip: HYPE only — withdrawal requests don't spend USDC.
  let wdMsg = null;
  if (lowHype) {
    wdMsg = `<span class="text-on-surface font-semibold">Low HYPE for gas.</span> `
      + `Withdrawals also pay HyperEVM gas in HYPE. Transfer some via the `
      + `<a href="${HYPE_BRIDGE_URL}" target="_blank" rel="noopener" class="text-primary underline">Hyperliquid portfolio</a>.`;
  }
  _setBalanceTip("wd", wdMsg);
}

function _setBalanceTip(prefix, html) {
  const wrap = document.getElementById(`${prefix}-balance-tip`);
  const msg  = document.getElementById(`${prefix}-balance-tip-msg`);
  if (!wrap || !msg) return;
  if (html) {
    msg.innerHTML = html;
    wrap.classList.remove("hidden");
  } else {
    msg.innerHTML = "";
    wrap.classList.add("hidden");
  }
}

// Reset HYPE display + tips back to neutral placeholders. Used by the
// disconnected/public preview path so the page never claims "0.000 HYPE"
// (which would imply we read 0 — we didn't read at all).
function _clearHypeDisplay() {
  for (const id of ["dep-hype-bal", "wd-hype-bal"]) {
    const el = document.getElementById(id);
    if (el) { el.textContent = "—"; el.className = "text-primary"; }
  }
  _setBalanceTip("dep", null);
  _setBalanceTip("wd", null);
}

function _setDepError(msg) {
  const errEl = document.getElementById("dep-error");
  const btn = document.getElementById("dep-btn");
  if (msg) {
    document.getElementById("dep-error-msg").textContent = msg;
    errEl.classList.remove("hidden");
    btn.disabled = true;
  } else {
    errEl.classList.add("hidden");
    btn.disabled = false;
  }
}

async function _updateDepPreview(vaultAddr) {
  const input = document.getElementById("dep-amount");
  const amount = parseFloat(input.value) || 0;
  if (amount <= 0) {
    document.getElementById("dep-preview").textContent = "0.0000";
    _setDepError(null);
    return;
  }

  const minDeposit = parseFloat(input.dataset.minDeposit) || 0;
  const usdcBal = parseFloat(document.getElementById("dep-usdc-bal").textContent) || 0;

  if (minDeposit > 0 && amount < minDeposit) {
    _setDepError(`Minimum deposit is $${minDeposit.toFixed(2)} USDC`);
    document.getElementById("dep-preview").textContent = "0.0000";
    return;
  }
  if (amount > usdcBal) {
    _setDepError(`Insufficient balance (${usdcBal.toFixed(2)} USDC available)`);
    document.getElementById("dep-preview").textContent = "0.0000";
    return;
  }
  _setDepError(null);

  const rp = getReadProvider();
  const vault = new ethers.Contract(vaultAddr, VAULT_ABI, rp);
  const assets = BigInt(Math.floor(amount * 10 ** USDC_DECIMALS));
  const shares = await vault.convertToShares(assets);
  document.getElementById("dep-preview").textContent = (Number(shares) / 10 ** SHARE_DECIMALS).toFixed(4);
}

async function _updateWdPreview(vaultAddr) {
  const shares = parseFloat(document.getElementById("wd-shares").value) || 0;
  const infoIcon = document.getElementById("wd-info");
  const perfNote = document.getElementById("wd-perf-note");
  if (shares <= 0) {
    document.getElementById("wd-preview").textContent = "0.00";
    infoIcon?.classList.add("hidden");
    perfNote?.classList.add("hidden");
    return;
  }
  const addr = getAddress();
  const rp = getReadProvider();
  const vault = new ethers.Contract(vaultAddr, VAULT_ABI, rp);
  const sharesBn = BigInt(Math.floor(shares * 10 ** SHARE_DECIMALS));
  const [grossAssets, userShares, userCostBasis, perfFeeBps] = await Promise.all([
    vault.convertToAssets(sharesBn),
    addr ? vault.balanceOf(addr) : Promise.resolve(0n),
    addr ? vault.userCostBasis(addr) : Promise.resolve(0n),
    vault.performanceFeeBps(),
  ]);
  const fee = computeWithdrawalFee(sharesBn, userShares, userCostBasis, grossAssets, perfFeeBps);
  const net = grossAssets - fee;
  const grossH = Number(grossAssets) / 10 ** USDC_DECIMALS;
  const feeH = Number(fee) / 10 ** USDC_DECIMALS;
  const netH = Number(net) / 10 ** USDC_DECIMALS;
  document.getElementById("wd-preview").textContent = netH.toFixed(2);
  if (fee > 0n) {
    const pctDisplay = Number(perfFeeBps) / 100;
    const profitH = grossH - ((Number(userCostBasis) / 10 ** USDC_DECIMALS) * Number(sharesBn) / Number(userShares || 1n));
    if (perfNote) {
      perfNote.textContent = `Includes ${pctDisplay}% perf fee on $${Math.max(0, profitH).toFixed(4)} profit.`;
      perfNote.classList.remove("hidden");
    }
    if (infoIcon) {
      infoIcon.setAttribute("title", `Gross $${grossH.toFixed(4)} − perf fee $${feeH.toFixed(4)} = net $${netH.toFixed(4)}.`);
      infoIcon.classList.remove("hidden");
    }
  } else {
    infoIcon?.classList.add("hidden");
    perfNote?.classList.add("hidden");
  }
}

async function _deposit(vaultAddr, userAddr) {
  // Defense-in-depth: never deposit into a vault closed to new deposits, even
  // if the disabled button state were somehow bypassed. Withdraw is unaffected.
  const _vc = VAULT_CONFIGS.find(v => v.address?.toLowerCase() === (vaultAddr || "").toLowerCase());
  if (_vc && _vc.deposit_closed === true) {
    _setDepError(`${_vc.name} is closed to new deposits. Existing positions can still be withdrawn.`);
    return;
  }
  const input = document.getElementById("dep-amount");
  const amount = parseFloat(input.value);
  if (!amount || amount <= 0) return;
  const minDeposit = parseFloat(input.dataset.minDeposit) || 0;
  if (minDeposit > 0 && amount < minDeposit) {
    _setDepError(`Minimum deposit is $${minDeposit.toFixed(2)} USDC`);
    return;
  }

  const signer = getSigner();
  const vault = new ethers.Contract(vaultAddr, VAULT_ABI, signer);
  const usdc = new ethers.Contract(USDC_ADDRESS, ERC20_ABI, signer);
  const assets = BigInt(Math.floor(amount * 10 ** USDC_DECIMALS));

  try {
    const allowance = await usdc.allowance(userAddr, vaultAddr);
    if (allowance < assets) {
      showTxModal("Approving USDC", "Please confirm in MetaMask...");
      const tx = await usdc.approve(vaultAddr, ethers.MaxUint256);
      document.getElementById("tx-message").textContent = "Waiting for approval...";
      await tx.wait();
    }

    showTxModal("Depositing " + amount.toFixed(2) + " USDC", "Please confirm in MetaMask...");
    const tx = await vault.deposit(assets, userAddr, { gasLimit: 500000 });
    document.getElementById("tx-message").textContent = "Transaction submitted...";
    const receipt = await tx.wait();

    const preview = document.getElementById("dep-preview").textContent;
    txModalSuccess("Deposited " + amount.toFixed(2) + " USDC for " + preview + " shares", receipt.hash);
    // Affiliate attribution — if the user landed via a /r/[code] link, the
    // cookie was set there. Fire-and-forget: deposit succeeded regardless,
    // so any backend hiccup here shouldn't surface as a deposit error. The
    // backend verifies the tx hash on-chain before recording the referral
    // (closes the front-running attack — see core/affiliate/onchain_verify.py).
    _maybeAttributeAffiliate({
      depositorWallet: userAddr,
      vaultAddr,
      depositBlock: receipt.blockNumber,
      depositTxHash: receipt.hash,
    });
    document.getElementById("dep-amount").value = "";
    _refreshVaultState(vaultAddr, userAddr);
    // Top-right nav balance is owned by app.js updateWalletUI(), which is
    // wired to the "wallet-changed" event. MetaMask doesn't fire that on
    // its own after a deposit (account didn't change, just its USDC bal),
    // so we have to nudge it manually — otherwise the nav stays stale at
    // pre-deposit balance until a full page reload.
    window.dispatchEvent(new Event("wallet-changed"));
  } catch (e) {
    txModalError(e.reason || e.shortMessage || "Deposit failed");
  }
}

async function _withdraw(vaultAddr, userAddr) {
  // Prefer the stashed raw BigInt over re-parsing the input string.
  // The MAX button sets `dataset.maxRaw` to the exact balance from
  // balanceOf(user). Re-parsing the displayed float would overshoot
  // when the 5th decimal rounds up via toFixed(4). User typing into
  // the input clears maxRaw (see the 'input' listener) so manual
  // amounts still go through the parseFloat path.
  const input = document.getElementById("wd-shares");
  let sharesBn;
  if (input.dataset.maxRaw) {
    sharesBn = BigInt(input.dataset.maxRaw);
    if (sharesBn <= 0n) return;
  } else {
    const shares = parseFloat(input.value);
    if (!shares || shares <= 0) return;
    sharesBn = BigInt(Math.floor(shares * 10 ** SHARE_DECIMALS));
  }

  const signer = getSigner();
  const vault = new ethers.Contract(vaultAddr, VAULT_ABI, signer);

  try {
    showTxModal("Requesting Withdrawal", "Please confirm in MetaMask...");
    const tx = await vault.requestWithdraw(sharesBn, { gasLimit: 300000 });
    document.getElementById("tx-message").textContent = "Transaction submitted...";
    const receipt = await tx.wait();

    txModalSuccess("Withdrawal requested. USDC will be available to claim shortly.", receipt.hash);
    const wdInput = document.getElementById("wd-shares");
    wdInput.value = "";
    delete wdInput.dataset.maxRaw;  // stale after tx — clear to avoid resubmit-with-old-balance
    _refreshVaultState(vaultAddr, userAddr);
    // See deposit-flow comment — keep the top-right nav balance in sync.
    window.dispatchEvent(new Event("wallet-changed"));
  } catch (e) {
    txModalError(e.reason || e.shortMessage || "Withdrawal failed");
  }
}

async function _claim(vaultAddr) {
  const signer = getSigner();
  const vault = new ethers.Contract(vaultAddr, VAULT_ABI, signer);

  try {
    showTxModal("Claiming USDC", "Please confirm in MetaMask...");
    const tx = await vault.claimWithdraw({ gasLimit: 200000 });
    document.getElementById("tx-message").textContent = "Transaction submitted...";
    const receipt = await tx.wait();

    txModalSuccess("USDC claimed to your wallet.", receipt.hash);
    _refreshVaultState(vaultAddr, getAddress());
    // See deposit-flow comment — keep the top-right nav balance in sync.
    // Claim is the most important of the three to refresh promptly: USDC
    // just landed in the user's wallet, the nav showing the OLD smaller
    // balance would feel like the claim didn't actually work.
    window.dispatchEvent(new Event("wallet-changed"));
  } catch (e) {
    txModalError(e.reason || e.shortMessage || "Claim failed");
  }
}
